// ########################## [IMPORTANT LIBRARIES]
import { useCallback, useMemo } from 'react';

// ########################## [TYPES]
import {
  EntityType,
  IBillingInfoInvoicesContext,
  BillingInformation,
  BillingInformationOptionalProperties,
} from '@shippypro/foundation/settings/types';

// ########################## [HOOKS]
import useLDFlag from '@web/hooks/useLDFlags';
import { useGetUser } from '@web/features/_global/hooks/api/useGetUser';
import { useValidateVatCode } from '@shippypro/foundation/settings/hooks/api/useValidateVatCode';
import { useGetRelatedEntities } from '@shippypro/foundation/settings/hooks/api/useGetRelatedEntities';
import { useGetBillingSettings } from '@shippypro/foundation/settings/hooks/api/useGetBillingSettings';
import { useUpdateBillingSettings } from '@shippypro/foundation/settings/hooks/api/useUpdateBillingSettings';
import useHasUserCompletedOnboardingOrSetup from '@web/features/_global/hooks/useHasUserCompletedOnboardingOrSetup';

/**
 * [HOOK] useInstanceBillingInfoInvoicesContext returns every data to be rendered about Billing info and invoices,
 * together with the actions to update those info on the `Billing info and invoices` tab
 *
 * @return {IBillingInfoInvoicesContext} - The data and actions on the Billing info and invoices tab
 * @author Fabio Mezzina <fabio.mezzina@shippypro.com>
 */
export const useInstanceBillingInfoInvoicesContext =
  (): IBillingInfoInvoicesContext => {
    const { user } = useGetUser();
    const { settingsOnUiApp } = useLDFlag();
    const [hasCompletedOnboarding] = useHasUserCompletedOnboardingOrSetup();

    // Get related company entity
    const { relatedEntities } = useGetRelatedEntities(
      user?.id,
      EntityType.User,
      { skip: !settingsOnUiApp || !hasCompletedOnboarding || !user?.id },
    );
    const workspaceId = useMemo(() => {
      const workspace = relatedEntities.find(
        entity => entity.entity_type === EntityType.Workspace,
      );
      return workspace?.entity_id;
    }, [relatedEntities]);

    // ------ [ METHODS SETUP ] ------ //
    const {
      billingInformation,
      isLoading: isGetBillingInformationLoading,
      isFetching: isGetBillingInformationFetching,
    } = useGetBillingSettings(workspaceId!);

    // Transform billingInformation to BillingInformation type
    const billingInfo = useMemo(
      () => billingInformation as BillingInformation,
      [billingInformation],
    );

    // BE call to update billing info
    const {
      updateBillingSettings,
      isLoading: isUpdateBillingInformationLoading,
    } = useUpdateBillingSettings(workspaceId!, EntityType.Workspace);

    const { validateVatCode, isLoading: isVatCodeValidationLoading } =
      useValidateVatCode();

    const validateVat = useCallback(
      async (vatCode: string, country: string) => {
        /* istanbul ignore else */
        if (settingsOnUiApp && workspaceId) {
          const result = await validateVatCode(vatCode, country);
          return result;
        } else return null;
      },
      [settingsOnUiApp, validateVatCode, workspaceId],
    );

    const setBillingInformation = useCallback(
      async (updatedBillingInfo: BillingInformationOptionalProperties) => {
        /* istanbul ignore else */
        if (settingsOnUiApp && workspaceId) {
          const result = await updateBillingSettings(updatedBillingInfo);
          return result;
        } else return null;
      },
      [settingsOnUiApp, updateBillingSettings, workspaceId],
    );

    return useMemo<IBillingInfoInvoicesContext>(
      () => ({
        billingInfo,
        isBillingInformationLoading:
          isUpdateBillingInformationLoading ||
          isGetBillingInformationFetching ||
          isUpdateBillingInformationLoading,
        isGetBillingInformationLoading,
        isUpdateBillingInformationLoading,
        isVatCodeValidationLoading,
        methods: {
          setBillingInformation,
          validateVat,
        },
      }),
      [
        billingInfo,
        isUpdateBillingInformationLoading,
        isGetBillingInformationFetching,
        isGetBillingInformationLoading,
        isVatCodeValidationLoading,
        setBillingInformation,
        validateVat,
      ],
    );
  };
